import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TextInput, Button, Card, Alert, Spinner } from 'flowbite-react';
import {
  HiSearch,
  HiDownload,
  HiMail,
  HiPhone,
  HiFilter,
  HiX,
  HiTag,
  HiOfficeBuilding,
  HiChevronDown
} from 'react-icons/hi';
import { DataTable, type ColumnDef } from '../components/tables';
import { PageHeader } from '../components/common/PageHeader';
import { SearchableSelect, matchesSearch } from '../components/common/SearchableSelect';
import { ExportOptionsModal, type ExportOptions } from '../components/modals/ExportOptionsModal';
import { contactsApi, type ContactSearchResult, type RoleTagOption } from '../api/contacts';
import { leasApi } from '../api/leas';
import { getUserErrorMessage } from '../api';
import { useAuth } from '../contexts/AuthContext';
import type { LEAListItem } from '../types/api';

export const SearchContactsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'superAdmin';
  const isAdmin = currentUser?.role === 'superAdmin' || currentUser?.role === 'organizationAdmin';

  // State
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false); // Track if a search is pending
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactSearchResult[]>([]);
  const [totalItems, setTotalItems] = useState(0); // Total count from server
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Pagination state for server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Export modal state (superAdmin only)
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filter states - updated names to reflect actual logic
  const [includeDistrictAdministrators, setIncludeDistrictAdministrators] = useState(true);
  const [includeBoard, setIncludeBoard] = useState(true);
  const [includeSolicitors, setIncludeSolicitors] = useState(true);
  const [includeSchoolAdministrators, setIncludeSchoolAdministrators] = useState(true);

  // New comprehensive filter states
  const [selectedOrganization, setSelectedOrganization] = useState<number | ''>('');
  const [selectedRoleTags, setSelectedRoleTags] = useState<string[]>([]);
  const [roleTagDropdownOpen, setRoleTagDropdownOpen] = useState(false);
  const [roleTagSearch, setRoleTagSearch] = useState('');
  const roleTagDropdownRef = useRef<HTMLDivElement>(null);
  const [organizations, setOrganizations] = useState<LEAListItem[]>([]);
  const [roleTags, setRoleTags] = useState<RoleTagOption[]>([]);

  // Refs for debouncing and preventing race conditions
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchRequestIdRef = useRef<number>(0);

  // Load organizations and role tags on mount
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        // Load organizations (API max limit is 500)
        const orgs = await leasApi.list({ limit: 500 });
        setOrganizations(orgs);

        // Load role tags from API
        try {
          const tags = await contactsApi.getRoleTags();
          setRoleTags(tags);
        } catch (error) {
          console.warn('Failed to load role tags, continuing without them:', error);
          // Don't fail the whole load if role tags fail
        }
      } catch (error) {
        console.error('Failed to load filter data:', error);
      }
    };

    loadFilterData();
  }, []);

  // Close role tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleTagDropdownRef.current && !roleTagDropdownRef.current.contains(event.target as Node)) {
        setRoleTagDropdownOpen(false);
        setRoleTagSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Organization filter options, grouped by LEA type. Types not listed here
  // (or missing) go under "Other", so no organization drops out of the list.
  const organizationOptions = useMemo(() => {
    const groups: Array<[string, string]> = [
      ['iu', 'Intermediate Unit'],
      ['district', 'Districts'],
      ['ctc', 'CTCs'],
      ['jointure', 'Jointures'],
      ['charter', 'Charter Networks'],
    ];
    const knownTypes = new Set(groups.map(([leaType]) => leaType));
    const byName = (a: LEAListItem, b: LEAListItem) => a.nameOfInstitution.localeCompare(b.nameOfInstitution);
    const toOptions = (orgs: LEAListItem[], group: string) =>
      [...orgs].sort(byName).map(org => ({ value: org.educationOrganizationId, label: org.nameOfInstitution, group }));

    return [
      ...groups.flatMap(([leaType, group]) => toOptions(organizations.filter(org => org.leaType === leaType), group)),
      ...toOptions(organizations.filter(org => !knownTypes.has(org.leaType ?? '')), 'Other'),
    ];
  }, [organizations]);

  const visibleRoleTags = useMemo(
    () => roleTags.filter(tag => matchesSearch({ label: tag.shortDescription }, roleTagSearch)),
    [roleTags, roleTagSearch]
  );

  // Toggle a role tag selection
  const toggleRoleTag = useCallback((codeValue: string) => {
    setSelectedRoleTags(prev =>
      prev.includes(codeValue)
        ? prev.filter(t => t !== codeValue)
        : [...prev, codeValue]
    );
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      selectedOrganization !== '' ||
      selectedRoleTags.length > 0 ||
      !includeDistrictAdministrators ||
      !includeBoard ||
      !includeSolicitors ||
      !includeSchoolAdministrators
    );
  }, [selectedOrganization, selectedRoleTags, includeDistrictAdministrators, includeBoard, includeSolicitors, includeSchoolAdministrators]);

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedOrganization('');
    setSelectedRoleTags([]);
    setIncludeDistrictAdministrators(true);
    setIncludeBoard(true);
    setIncludeSolicitors(true);
    setIncludeSchoolAdministrators(true);
  };

  // Reset to page 1 when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedOrganization, selectedRoleTags, includeDistrictAdministrators, includeBoard, includeSolicitors, includeSchoolAdministrators, showInactive]);

  // Sync search term to URL (separate from the search effect to avoid
  // an infinite loop — searchParams/setSearchParams must NOT be deps of
  // the search effect because setSearchParams produces a new searchParams
  // reference, which would re-trigger the effect endlessly).
  useEffect(() => {
    setSearchParams(prev => {
      if (searchTerm) {
        prev.set('search', searchTerm);
      } else {
        prev.delete('search');
      }
      return prev;
    }, { replace: true });
  }, [searchTerm, setSearchParams]);

  // Effect to search when filters, search term, or page change
  useEffect(() => {
    // Set searching state to show pending indicator
    setSearching(true);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the search
    searchTimeoutRef.current = setTimeout(async () => {
      // Increment request ID to track this specific request
      const currentRequestId = ++searchRequestIdRef.current;

      try {
        setLoading(true);
        setSearching(false);
        setError(null);

        // Calculate skip for server-side pagination
        const skip = (currentPage - 1) * pageSize;

        const response = await contactsApi.search({
          search: searchTerm || undefined,
          organizationId: selectedOrganization || undefined,
          roleTag: selectedRoleTags.length > 0 ? selectedRoleTags.join(',') : undefined,
          includeAdministrators: includeDistrictAdministrators,
          includeBoard,
          includeSolicitors,
          includePrincipals: includeSchoolAdministrators,
          includeInactive: showInactive,
          skip,
          limit: pageSize
        });

        // Only update state if this is still the latest request
        // This prevents stale responses from overwriting newer results
        if (currentRequestId === searchRequestIdRef.current) {
          setContacts(response.results);
          setTotalItems(response.total);
        }
      } catch (err: any) {
        // Only update error state if this is still the latest request
        if (currentRequestId === searchRequestIdRef.current) {
          setError(getUserErrorMessage(err, 'Failed to search contacts'));
          setContacts([]);
          setTotalItems(0);
        }
      } finally {
        // Only clear loading if this is still the latest request
        if (currentRequestId === searchRequestIdRef.current) {
          setLoading(false);
        }
      }
    }, 300);

    // Cleanup timeout on unmount or before next effect run
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedOrganization, selectedRoleTags, includeDistrictAdministrators, includeBoard, includeSolicitors, includeSchoolAdministrators, showInactive, currentPage, pageSize]);

  // Handle page change from DataTable
  const handlePageChange = (page: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to page 1 when page size changes
    } else {
      setCurrentPage(page);
    }
  };

  // Export button click handler
  const handleExportClick = () => {
    if (isAdmin) {
      // Show export options modal for superAdmin and orgAdmin (can include addresses)
      setShowExportModal(true);
    } else {
      // Direct export for viewer users
      performExport({ includeAddresses: false, format: 'xlsx' });
    }
  };

  // Perform the actual export
  const performExport = async (options: ExportOptions) => {
    try {
      setIsExporting(true);
      const blob = await contactsApi.export({
        search: searchTerm || undefined,
        organizationId: selectedOrganization || undefined,
        roleTag: selectedRoleTags.length > 0 ? selectedRoleTags.join(',') : undefined,
        includeAdministrators: includeDistrictAdministrators,
        includeBoard,
        includeSolicitors,
        includePrincipals: includeSchoolAdministrators,
        includeInactive: showInactive,
        format: options.format,
        includeAddresses: options.includeAddresses
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = options.format === 'xlsx' ? 'xlsx' : options.format === 'csv' ? 'csv' : 'json';
      a.download = `contacts_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
    } catch (err: any) {
      setError('Failed to export contacts');
    } finally {
      setIsExporting(false);
    }
  };

  // Table columns
  const columns: ColumnDef<ContactSearchResult>[] = useMemo(() => [
    {
      id: 'displayName',
      header: 'Name',
      accessor: 'displayName',
      sortable: true,
      searchable: true,
      render: (value, row) => {
        // Handle org email results differently
        if (row.resultType === 'organizationEmail') {
          return (
            <div className="flex items-center gap-2">
              <HiOfficeBuilding className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">{value}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Organization Email</div>
              </div>
            </div>
          );
        }

        // Regular person result
        return (
          <Link
            to={`/persons/${row.personId}`}
            className="text-left hover:underline block"
          >
            <div className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">{value}</div>
            {row.positionTitle && (
              <div className="text-sm text-gray-600 dark:text-gray-400">{row.positionTitle}</div>
            )}
          </Link>
        );
      }
    },
    {
      id: 'organizationName',
      header: 'Organization',
      accessor: 'organizationName',
      sortable: true,
      searchable: true,
      render: (value, row) => {
        // For LEAs, use leaId; for Schools, use parent LEA ID
        const targetLeaId = row.organizationType === 'LEA' ? row.leaId : row.parentLeaId;

        return (
          <div className="space-y-1">
            {targetLeaId ? (
              <Link
                to={`/leas/${targetLeaId}`}
                className="text-left hover:underline block"
              >
                <div className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                  {value}
                </div>
              </Link>
            ) : (
              <div className="font-medium text-gray-900 dark:text-white">{value}</div>
            )}

            {/* Show district name for schools */}
            {row.organizationType === 'School' && row.parentLeaName && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {row.parentLeaName}
              </div>
            )}

            {/* Show LEA type for LEAs */}
            {row.organizationType === 'LEA' && row.leaType && (
              <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{row.leaType}</div>
            )}
          </div>
        );
      }
    },
    {
      id: 'contact',
      header: 'Contact',
      accessor: 'email',
      sortable: true,
      searchable: true,
      render: (_value, row) => (
        <div className="space-y-1">
          {row.email ? (
            <a
              href={`mailto:${row.email}`}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={(e) => e.stopPropagation()}
            >
              <HiMail className="mr-1 h-4 w-4" />
              {row.email}
            </a>
          ) : null}
          {row.phone ? (
            <a
              href={`tel:${row.phone}`}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={(e) => e.stopPropagation()}
            >
              <HiPhone className="mr-1 h-4 w-4" />
              {row.phone}
            </a>
          ) : null}
          {!row.email && !row.phone && (
            <span className="text-sm text-gray-400 dark:text-gray-500 italic">No contact info</span>
          )}
        </div>
      )
    }
  ], []);

  return (
    <div className="container mx-auto p-8">
      <PageHeader
        title="Search Contacts"
        subtitle="Find administrators, board members, and staff across all organizations"
      />

      {/* Search and Filters Card */}
      <Card className="mb-6">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <div className="absolute top-1/2 -translate-y-1/2 left-0 flex items-center pl-3 pointer-events-none">
                <HiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <TextInput
                type="search"
                placeholder="Search by name, email, phone, organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10"
                autoFocus
              />
              {/* Loading indicator in search bar */}
              {searching && !loading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner size="sm" color="gray" />
                </div>
              )}
            </div>
            <Button
              color="light"
              onClick={() => setShowFilters(!showFilters)}
              className="text-gray-600"
            >
              <HiFilter className="mr-2 h-4 w-4" />
              Filters
              {showFilters && <HiX className="ml-2 h-4 w-4" />}
            </Button>
            {hasActiveFilters && (
              <Button
                color="light"
                onClick={handleClearFilters}
                className="text-red-600 hover:text-red-700"
              >
                <HiX className="mr-2 h-4 w-4" />
                Clear Filters
              </Button>
            )}
            <Button
              color="light"
              onClick={handleExportClick}
              disabled={contacts.length === 0 || isExporting}
              className="text-blue-600 hover:text-blue-700"
            >
              <HiDownload className="mr-2 h-4 w-4" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>

            {/* Show Inactive Checkbox - SuperAdmin Only */}
            {isSuperAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                />
                Show inactive
              </label>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="border-t dark:border-gray-700 pt-4 space-y-4">
              {/* Dropdown Filters Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Organization Filter */}
                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Organization
                  </label>
                  <SearchableSelect
                    id="organization"
                    options={organizationOptions}
                    value={selectedOrganization === '' ? null : selectedOrganization}
                    onChange={(orgId) => setSelectedOrganization(orgId ?? '')}
                    clearLabel="All Organizations"
                    className="w-full"
                  />
                </div>

                {/* Role Tag Multi-Select Filter */}
                <div ref={roleTagDropdownRef} className="relative">
                  <label
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    title="Standard roles, matched whatever each organization calls the position"
                  >
                    <HiTag className="inline-block w-4 h-4 mr-1" />
                    Role Tags
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setRoleTagDropdownOpen(!roleTagDropdownOpen);
                      setRoleTagSearch('');
                    }}
                    className="flex items-center justify-between w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500"
                  >
                    <span className="truncate">
                      {selectedRoleTags.length === 0
                        ? 'All Roles'
                        : selectedRoleTags.length === 1
                          ? roleTags.find(t => t.codeValue === selectedRoleTags[0])?.shortDescription || selectedRoleTags[0]
                          : `${selectedRoleTags.length} roles selected`}
                    </span>
                    <HiChevronDown className={`ml-2 h-4 w-4 flex-shrink-0 transition-transform ${roleTagDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {roleTagDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg">
                      <div className="sticky top-0 bg-white dark:bg-gray-700 p-2 border-b border-gray-200 dark:border-gray-600">
                        <input
                          type="text"
                          autoFocus
                          value={roleTagSearch}
                          onChange={(e) => setRoleTagSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setRoleTagDropdownOpen(false);
                              setRoleTagSearch('');
                            }
                          }}
                          placeholder="Search roles..."
                          aria-label="Search role tags"
                          className="block w-full rounded-md border border-gray-300 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600 dark:text-white dark:placeholder-gray-400"
                        />
                      </div>
                      {selectedRoleTags.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedRoleTags([])}
                          className="w-full px-3 py-1.5 text-xs text-left text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-200 dark:border-gray-600"
                        >
                          Clear selection
                        </button>
                      )}
                      {visibleRoleTags.length === 0 && (
                        <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching roles</p>
                      )}
                      {visibleRoleTags.map(tag => (
                        <label
                          key={tag.codeValue}
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 text-sm text-gray-900 dark:text-white"
                        >
                          <input
                            type="checkbox"
                            checked={selectedRoleTags.includes(tag.codeValue)}
                            onChange={() => toggleRoleTag(tag.codeValue)}
                            className="rounded border-gray-300 dark:border-gray-500 text-blue-600 focus:ring-blue-500 dark:bg-gray-600"
                          />
                          {tag.shortDescription}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Checkbox Filters Row - Compact Style */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Contact Types</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setIncludeDistrictAdministrators(!includeDistrictAdministrators)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      includeDistrictAdministrators
                        ? 'bg-[#004C97] text-white hover:bg-[#003d7a]'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {includeDistrictAdministrators && (
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    District Administrators
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeBoard(!includeBoard)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      includeBoard
                        ? 'bg-[#004C97] text-white hover:bg-[#003d7a]'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {includeBoard && (
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    Board Members
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeSolicitors(!includeSolicitors)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      includeSolicitors
                        ? 'bg-[#004C97] text-white hover:bg-[#003d7a]'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {includeSolicitors && (
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    Solicitors
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeSchoolAdministrators(!includeSchoolAdministrators)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      includeSchoolAdministrators
                        ? 'bg-[#004C97] text-white hover:bg-[#003d7a]'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {includeSchoolAdministrators && (
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    School Administrators
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Results */}
      {error && (
        <Alert color="failure" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Card>
          <div className="space-y-4">
            {/* Animated search header */}
            <div className="flex items-center gap-3 mb-6">
              <Spinner size="md" color="blue" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Searching contacts...</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {searchTerm ? `Looking for "${searchTerm}"` : 'Loading all contacts'}
                  {includeDistrictAdministrators && includeBoard && includeSolicitors && includeSchoolAdministrators ? '' : ' with filters applied'}
                </p>
              </div>
            </div>

            {/* Skeleton loader for table rows */}
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="border dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                      </div>
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                      </div>
                      <div className="space-y-2 flex-1">
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {contacts.length === 0 ? (
                searchTerm ? 'No results found' : 'Enter a search term to begin'
              ) : (
                <>
                  Found {totalItems} contact{totalItems !== 1 ? 's' : ''}
                  {searching && !loading && (
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-normal flex items-center gap-1">
                      <Spinner size="xs" color="gray" />
                      Updating...
                    </span>
                  )}
                </>
              )}
            </h3>
          </div>

          {contacts.length > 0 && (
            <DataTable
              data={contacts}
              columns={columns}
              searchable={false}
              sortable={true}
              pagination={true}
              pageSize={pageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              selectable={false}
              hoverable={true}
              striped={false}
              emptyMessage="No contacts found matching your search."
              ariaLabel="Contact search results"
              // Server-side pagination props
              serverSide={true}
              totalItems={totalItems}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      )}

      {/* Export Options Modal - SuperAdmin Only */}
      {showExportModal && (
        <ExportOptionsModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={performExport}
          isExporting={isExporting}
        />
      )}
    </div>
  );
};